import React from "react"
import { FormikValues } from "formik"
import { StrictOmit } from "shared/Types/helperTypes"
import { UnreachableCaseError } from "shared/Helpers/lang"
import {
    FormikAutocompleteControl,
    FormikCheckbox,
    FormikCheckboxProps,
    FormikSelectControl,
    FormikTextField,
    FormikTextFieldProps
} from "shared/Components/Form/Formik"
import { FormInputField, InputType, ValidationType } from "../formTypes"
import ExtraServicesControl from "../../../Components/Form/ExtraServicesControl"
import { DeliveryPayloadType, IDeliveryFormExtraServiceItem } from "../../../Types/MeetingPlanning/deliveryForm"
import { DynamicAutocomplete } from "../../../Components/Form/DynamicAutocomplete"
import { makeStyles, Typography } from "@material-ui/core"
import AccountingDimensionTextField from "../../../Components/Form/AccountingDimensionTextField"
import { NumberSelectControl } from "../../../Components/Form/NumberSelectControl"
import {
    FormikDeliverySelectControl,
    OtherDeliveryControl
} from "../../../Components/Form/DeliveryControls"
import TextareaControl from "../../../Components/Form/TextareaControl"

type DynamicFormFieldProps = Readonly<{
    field: FormInputField
    textFieldProps?: StrictOmit<FormikTextFieldProps<FormikValues>, "type" | "name" | "label" | "placeholder" | "helperText" | "inputProps" | "autoComplete" | "autoFocus" | "disabled">
    checkboxProps?: StrictOmit<FormikCheckboxProps<FormikValues>, "name" | "placeholder" | "helperText" | "disabled" | "children">
    setSelectedServices?: (services: IDeliveryFormExtraServiceItem[]) => {}
    onDeliveryChange?: (deliveryType: DeliveryPayloadType) => void
}>

const useStyles = makeStyles((theme) => ({
    link: {
        textDecoration: "underline",
        width: "100%",
        textAlign: "center",
        fontWeight: 500,
        cursor: "pointer"
    }
}))

export function DynamicFormField({ field, textFieldProps = {}, checkboxProps = {}, setSelectedServices, onDeliveryChange }: DynamicFormFieldProps) {
    const classes = useStyles()

    switch (field.type) {
        case InputType.ACCOUNTING_DIMENSION_TEXT_FIELD:
            return (
                <AccountingDimensionTextField
                    fullWidth
                    type={field.inputType}
                    name={field.name}
                    variant={field.variant || 'standard'}
                    title={field.title || ''}
                    dimensionId={field?.dimensionId || undefined}
                    label={field.label}
                    placeholder={field.placeholder}
                    helperText={field.helperText}
                    validations={field.validations}
                    inputProps={{ inputMode: field.inputMode }}
                    autoComplete={field.autoComplete}
                    autoFocus={field.autoFocus}
                    disabled={field.disabled}
                    {...textFieldProps}
                />
            )
        case InputType.TEXT:
            return (
                <FormikTextField
                    fullWidth
                    type={field.inputType}
                    name={field.name}
                    variant={field.variant || 'standard'}
                    title={field.title || ''}
                    label={field.label}
                    active={field.isActive ?? false}
                    required={field.required}
                    placeholder={field.placeholder}
                    helperText={field.helperText}
                    inputProps={{ inputMode: field.inputMode }}
                    autoComplete={field.autoComplete}
                    autoFocus={field.autoFocus}
                    disabled={field.disabled}
                    {...textFieldProps}
                />
            )
        case InputType.TEXTAREA:
            return (
                <TextareaControl
                    field={field}
                />
            )
        case InputType.CHECKBOX:
            return (
                <FormikCheckbox
                    name={field.name}
                    placeholder={field.placeholder}
                    helperText={field.helperText}
                    disabled={field.disabled}
                    {...checkboxProps}
                >
                    {field.label}
                </FormikCheckbox>
            )
        case InputType.DELIVERY_SELECT:
            return (
                <FormikDeliverySelectControl
                    name={field.name}
                    label={typeof field.label === "string" ? field.label : ""}
                    placeholder={field.placeholder}
                    options={field.options ?? []}
                    required={field.required}
                    onDeliveryChange={(deliveryType) => onDeliveryChange ? onDeliveryChange(deliveryType) : {}}
                />
            )
        case InputType.SELECT:
            return (
                <FormikSelectControl
                    name={field.name}
                    label={typeof field.label === "string" ? field.label : ""}
                    placeholder={field.placeholder}
                    options={field.options ?? []}
                    required={field.required}
                />
            )
        case InputType.OTHER_DELIVERY_COMPONENT:
            return (
                <OtherDeliveryControl
                    field={field}
                />
            )
        case InputType.DYNAMIC_AUTOCOMPLETE:
            return (
                <DynamicAutocomplete field={field} />
            )
        case InputType.NUMBER_SELECT:
            return (
                <NumberSelectControl
                    name={field.name}
                    required={field.required}
                    label={field.label as string}
                    placeholder={field.placeholder}
                />
            )
        case InputType.BUTTON:
            return (
                <Typography variant="body1" className={classes.link} onClick={field.action}>{ field.label }</Typography>
            )
        case InputType.EXTRA_SERVICES:
            return (
                <ExtraServicesControl name={field.name} seats={0} selectedServices={[]} setSelectedServices={() => setSelectedServices ? setSelectedServices : {}} />
            )
        default:
            throw new UnreachableCaseError(field)
    }
}
